import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchNotificationPreferences,
  saveNotificationPreferences,
} from "../thunks/notificationPreferences.thunk";
import type {
  NotificationPreferenceScope,
  NotificationPreferencesMap,
} from "../interfaces/notificationPreferences.interface";
import { logout } from "../../auth/slices/auth.slice";
import { logoutUser } from "../../auth/thunks/auth.thunk";

interface NotificationPreferencesState {
  userPreferences: NotificationPreferencesMap | null;
  adminPreferences: NotificationPreferencesMap | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
}

const initialState: NotificationPreferencesState = {
  userPreferences: null,
  adminPreferences: null,
  loading: false,
  saving: false,
  error: null,
};

const notificationPreferencesSlice = createSlice({
  name: "notificationPreferences",
  initialState,
  reducers: {
    resetNotificationPreferences: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => initialState);
    builder.addCase(logoutUser.fulfilled, () => initialState);
    builder.addCase(logoutUser.rejected, () => initialState);

    builder.addCase(fetchNotificationPreferences.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      fetchNotificationPreferences.fulfilled,
      (
        state,
        action: PayloadAction<{
          scope: NotificationPreferenceScope;
          preferences: NotificationPreferencesMap;
        }>,
      ) => {
        state.loading = false;
        if (action.payload.scope === "admin") {
          state.adminPreferences = action.payload.preferences;
        } else {
          state.userPreferences = action.payload.preferences;
        }
      },
    );
    builder.addCase(fetchNotificationPreferences.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as { message?: string })?.message ?? null;
    });

    builder.addCase(saveNotificationPreferences.pending, (state) => {
      state.saving = true;
      state.error = null;
    });
    builder.addCase(
      saveNotificationPreferences.fulfilled,
      (
        state,
        action: PayloadAction<{
          scope: NotificationPreferenceScope;
          preferences: NotificationPreferencesMap;
        }>,
      ) => {
        state.saving = false;
        if (action.payload.scope === "admin") {
          state.adminPreferences = action.payload.preferences;
        } else {
          state.userPreferences = action.payload.preferences;
        }
      },
    );
    builder.addCase(saveNotificationPreferences.rejected, (state, action) => {
      state.saving = false;
      state.error = (action.payload as { message?: string })?.message ?? null;
    });
  },
});

export const { resetNotificationPreferences } =
  notificationPreferencesSlice.actions;
export default notificationPreferencesSlice.reducer;
